import React, { useState, useEffect, useRef } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import Header from "./components/Header";
import MainContent from "./components/MainContent";
import Footer from "./components/Footer";
import TermsOfUse from "./components/TermsOfUse";
import TermsOfServiceEnterprise from "./components/TermsOfServiceEnterprise";

import FaqConsumer from "./components/FaqConsumer";
import FaqEnterprise from "./components/FaqEnterprise";
import AboutFozzil from "./components/AboutFozzil";
import PrivacyPolicy from "./components/PrivacyPolicy";
import {
  EthicsAndCompliance,
  EthicsCompliance,
} from "./components/EthicsAndCompliance";
import AWM from "./components/AWM";
import ZYPHERAI from "./components/ZYPHERAI";
import Taels from "./components/Taels";
import FozzilArk from "./components/FozzilArk";
import PricingTaels from "./components/PricingTaels";
import PricingMedos from "./components/PricingMedos";
import PaymentSuccess from "./components/PaymentSuccess";
import Brand from "./components/Brand";
import AcceptableUsePolicy from "./components/AcceptableUsePolicy";
import CookiePolicy from "./components/CookiePolicy";
import Legal from "./components/Legal";
import ComingSoon from "./components/ComingSoon";
import CancellationAndRefund from "./components/CancellationAndRefund";
import Login from "./components/Login";
import Signup from "./components/Signup";

import SecurityAndPrivacy from "./components/SecurityAndPrivacy";
import MEDOS from "./components/MEDOS";
import Jobs from "./components/Jobs";
import JobDetails from "./components/JobDetails";

import FozzilAccount from "./components/FozzilAccount";
import PaymentRequired from "./components/PaymentRequired";
import CookieConsent from "./components/CookieConsent";
import AiMuseum from "./components/AiMuseum";
import StoriesCompany from "./components/stories/StoriesCompany";
import StoriesAiMuseum from "./components/stories/StoriesAiMuseum";
import StoriesArk from "./components/stories/StoriesArk";
import { useAuth } from "./contexts/AuthContext";
import { Navigate } from "react-router-dom";
import StoreLanding from "./components/store/StoreLanding";
import ShopArk from "./components/store/ShopArk";
import ShopMedos from "./components/store/ShopMedos";
import ShopIntelligence from "./components/store/ShopIntelligence";
import BuyArk from "./components/store/BuyArk";
import BuyArkPro from "./components/store/BuyArkPro";
import BagPage from "./components/store/BagPage";
import CheckoutPage from "./components/store/CheckoutPage";
import OrdersPage from "./components/store/OrdersPage";
import SavesPage from "./components/store/SavesPage";
import { RegionProvider } from "./contexts/RegionContext";
import { BagProvider } from "./contexts/BagContext";
import { SavesProvider } from "./contexts/SavesContext";

const ExternalRedirect = ({ path }) => {
  const location = useLocation();
  const { isAuthenticated, isLoading } = useAuth();
  
  useEffect(() => {
    if (isLoading) return; // Wait for session check
    
    // Determine return path
    const currentHost = window.location.hostname.toLowerCase();
    const isAccountHost = currentHost.includes('account.fozzil.in');
    const isStoreHost = currentHost.includes('store.fozzil.in');
    
    let returnPath;
    if (isAccountHost) {
      returnPath = '/account';
    } else if (location.state?.from?.pathname) {
      returnPath = location.state.from.pathname;
    } else if (isStoreHost || window.location.pathname.startsWith('/shop')) {
      returnPath = '/shop';
    } else {
      returnPath = '/';
    }

    // IF ALREADY AUTHENTICATED: Skip the auth hub and just go where they wanted to go
    if (isAuthenticated) {
      window.location.replace(window.location.origin + returnPath);
      return;
    }
    
    const authUrl = process.env.REACT_APP_AUTH_URL || "https://auth.fozzil.in";
    const returnUrl = window.location.origin + returnPath;
    
    let product = 'fozzil';
    if (isStoreHost || returnPath.startsWith('/shop')) {
      product = 'store';
    } else if (isAccountHost || returnPath.startsWith('/account')) {
      product = 'account';
    } else if (currentHost.includes('fi-') || currentHost.includes('intelligence')) {
      product = 'intelligence';
    } else if (currentHost.includes('ecosystem.fozzil.in')) {
      product = 'ecosystem';
    }
    
    const separator = path.includes('?') ? '&' : '?';
    let deviceId = localStorage.getItem('fozzil_did');
    if (!deviceId) {
      deviceId = crypto.randomUUID();
      localStorage.setItem('fozzil_did', deviceId);
    }
    const payload = btoa(JSON.stringify({ t: Date.now(), r: returnUrl, c: product, d: deviceId }));
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const cookieDomain = isLocalhost ? '' : 'domain=.fozzil.in;';
    document.cookie = `shade_token=${payload}; ${cookieDomain} path=/; max-age=60; secure; samesite=lax`;

    // Forward shade_token and dev_key so SHADE validation on auth.fozzil.in succeeds across domains
    const devParam = isLocalhost ? '&dev_key=fozzil_local_dev_token_9988' : '';
    const redirectTarget = `${authUrl}${path}${separator}redirect=${encodeURIComponent(returnUrl)}&shade_token=${encodeURIComponent(payload)}${devParam}`;
    window.location.replace(redirectTarget);
  }, [path, location.state, isAuthenticated, isLoading]);
  
  return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <div className="relative flex items-center justify-center w-16 h-16">
        <div className="absolute inset-0 bg-gradient-to-tr from-blue-600/40 via-cyan-400/30 to-blue-500/40 blur-xl rounded-full taels-gemini-glow" />
        <div className="absolute -inset-1 rounded-full taels-conic-ring pointer-events-none" />
        <div className="relative z-10 w-11 h-11 rounded-full overflow-hidden flex items-center justify-center bg-black shadow-2xl border border-white/20 taels-gemini-pulse">
          <img src="/taels-logo-black.png" alt="TAELS" className="w-full h-full rounded-full object-cover select-none pointer-events-none" />
        </div>
      </div>
    </div>
  );
};

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="relative flex items-center justify-center w-16 h-16">
          <div className="absolute inset-0 bg-gradient-to-tr from-blue-600/40 via-cyan-400/30 to-blue-500/40 blur-xl rounded-full taels-gemini-glow" />
          <div className="absolute -inset-1 rounded-full taels-conic-ring pointer-events-none" />
          <div className="relative z-10 w-11 h-11 rounded-full overflow-hidden flex items-center justify-center bg-black shadow-2xl border border-white/20 taels-gemini-pulse">
            <img src="/taels-logo-black.png" alt="TAELS" className="w-full h-full rounded-full object-cover select-none pointer-events-none" />
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  return children;
};

function AppContent() {
  const location = useLocation();
  const { isAuthenticated, isLoading, isRecovering } = useAuth();
  const navigate = useNavigate();

  // Handle password recovery redirect
  useEffect(() => {
    if (isRecovering) {
      navigate("/auth", { state: { from: { pathname: location.pathname } } });
    }
  }, [isRecovering, navigate, location.pathname]);

  const hideNavAndFooter =
    location.pathname === "/auth" ||
    location.pathname === "/signup" ||
    location.pathname === "/pricing" ||
    location.pathname === "/pricing-taels" ||
    location.pathname === "/pricing-intelligence" ||
    location.pathname === "/pricing-medos";
  const hideFooter =
    location.pathname === "/products/pricing" ||
    location.pathname === "/pricing" ||
    location.pathname === "/pricing-taels" ||
    location.pathname === "/pricing-intelligence" ||
    location.pathname === "/pricing-medos" ||
    location.pathname === "/shop/checkout" ||
    hideNavAndFooter;
  const companyPaths = [
    "/about",

    "/privacy",
    "/terms",
    "/ethics-compliance",
  ];
  const isCompanyPage = companyPaths.includes(location.pathname);
  const isHome = location.pathname === "/";
  const isPricingOrPayment =
    location.pathname === "/pricing" ||
    location.pathname === "/pricing-taels" ||
    location.pathname === "/pricing-intelligence" ||
    location.pathname === "/pricing-medos";
  const currentHost = window.location.hostname.toLowerCase();
  const isAccountDomain = currentHost.includes("account.fozzil.in") || window.location.href.includes("account.fozzil.in");
  const isStoreDomain = currentHost.includes("store.fozzil.in") || window.location.href.includes("store.fozzil.in");

  const isAccountPage =
    location.pathname === "/account" || (location.pathname === "/" && isAccountDomain);

  const isStorePage = location.pathname.startsWith('/shop') || (location.pathname === "/" && isStoreDomain);

  // Footer intersection logic
  const footerRef = useRef(null);
  const [footerInView, setFooterInView] = useState(false);
  useEffect(() => {
    const observer = new window.IntersectionObserver(
      ([entry]) => setFooterInView(entry.isIntersecting),
      { threshold: 0.1 }
    );
    if (footerRef.current) observer.observe(footerRef.current);
    return () => observer.disconnect();
  }, []);

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Subdomain & Path Enforcement logic
  useEffect(() => {
    const currentHost = window.location.hostname.toLowerCase();
    if (currentHost === 'localhost') return; // Don't redirect on localhost

    const mainUrl = "https://fozzil.in";
    const accountUrl = process.env.REACT_APP_ACCOUNT_URL || "https://account.fozzil.in";
    const storeUrl = process.env.REACT_APP_STORE_URL || "https://store.fozzil.in";

    const isStoreHost = currentHost.includes("store.fozzil.in");
    const isAccountHost = currentHost.includes("account.fozzil.in");
    const isMainHost = !isStoreHost && !isAccountHost;

    // 1. Enforce Account Domain
    if (location.pathname === "/account" && !isAccountHost) {
      window.location.replace(`${accountUrl}/account${location.search}`);
      return;
    }

    // 2. Enforce Store Domain
    if (location.pathname.startsWith("/shop") && !isStoreHost) {
      window.location.replace(`${storeUrl}${location.pathname}${location.search}`);
      return;
    }

    // 3. Prevent non-account pages on account subdomain
    const accountPaths = ["/", "/account", "/Sign-In and Security", "/Payment and shipping", "/Subscriptions", "/Devices", "/Appearance", "/Privacy"];
    const decodedPath = decodeURIComponent(location.pathname);
    if (isAccountHost && !accountPaths.includes(decodedPath)) {
      window.location.replace(`${mainUrl}${location.pathname}${location.search}`);
      return;
    }

    // 4. Prevent non-shop pages on store subdomain
    // Note: root "/" on store.fozzil.in is allowed as it shows the shop landing
    if (isStoreHost && location.pathname !== "/" && !location.pathname.startsWith("/shop")) {
      window.location.replace(`${mainUrl}${location.pathname}${location.search}`);
      return;
    }
  }, [location.pathname, location.search]);

  // Adjust mobile backdrop-filter based on route
  useEffect(() => {
    if (location.pathname.startsWith("/account")) {
      document.body.classList.add("allow-mobile-backdrop");
    } else {
      document.body.classList.remove("allow-mobile-backdrop");
    }
  }, [location.pathname]);

  const hideHeader = 
    hideNavAndFooter || 
    location.pathname === "/account" ||
    isAccountDomain;

  return (
    <div className={`App ${!hideHeader ? 'pt-[48px]' : ''}`}>
      {!hideHeader && <Header />}
      <Routes>
        <Route
          path="/"
          element={
            isAccountDomain ? (
              <ProtectedRoute>
                <FozzilAccount />
              </ProtectedRoute>
            ) : isStoreDomain ? (
              <StoreLanding />
            ) : (
              <MainContent />
            )
          }
        />
        <Route path="/about" element={<AboutFozzil />} />
        <Route path="/terms" element={<TermsOfUse />} />
        <Route path="/contact" element={<div dangerouslySetInnerHTML={{ __html: "<script>window.location.href='https://support.fozzil.in/contact'</script>" }} />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/brand" element={<Brand />} />
        <Route path="/auth" element={<ExternalRedirect path="/auth" />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<ExternalRedirect path="/signup" />} />
        <Route
          path="/ethics-compliance"
          element={
            <>
              <EthicsAndCompliance />
              <EthicsCompliance />
            </>
          }
        />
        <Route path="/research/awm" element={<AWM />} />
        <Route path="/research/ai-museum" element={<AiMuseum />} />
        <Route path="/zypherai" element={<ZYPHERAI />} />
        <Route path="/taels" element={<Taels />} />
        <Route path="/fozzil-intelligence" element={<Taels />} />
        <Route path="/fozzil-ark" element={<FozzilArk />} />
        <Route path="/pricing-taels" element={<PricingTaels />} />
        <Route path="/pricing-intelligence" element={<PricingTaels />} />
        <Route path="/pricing" element={<PricingTaels />} />
        <Route path="/pricing-medos" element={<PricingMedos />} />
        <Route path="/payment-success" element={<PaymentSuccess />} />
        <Route
          path="/acceptable-use-policy"
          element={<AcceptableUsePolicy />}
        />
        <Route path="/cookie-policy" element={<CookiePolicy />} />
        <Route path="/legal" element={<Legal />} />
        <Route path="/terms-enterprise" element={<TermsOfServiceEnterprise />} />
        <Route path="/faq-consumer" element={<FaqConsumer />} />
        <Route path="/faq-enterprise" element={<FaqEnterprise />} />

        <Route path="/security-and-privacy" element={<SecurityAndPrivacy />} />
        <Route path="/coming-soon" element={<ComingSoon />} />
        <Route
          path="/cancellation-and-refund"
          element={<CancellationAndRefund />}
        />
        <Route path="/stories/company" element={<StoriesCompany />} />
        <Route path="/stories/ai-museum" element={<StoriesAiMuseum />} />
        <Route path="/stories/ark" element={<StoriesArk />} />
        <Route path="/products/medos" element={<MEDOS />} />
        <Route path="/medos" element={<MEDOS />} />
        <Route path="/jobs" element={<Jobs />} />
        <Route path="/jobs/:jobId" element={<JobDetails />} />

        <Route path="/account" element={<ProtectedRoute><FozzilAccount /></ProtectedRoute>} />
        <Route path="/Sign-In and Security" element={<ProtectedRoute><FozzilAccount /></ProtectedRoute>} />
        <Route path="/Payment and shipping" element={<ProtectedRoute><FozzilAccount /></ProtectedRoute>} />
        <Route path="/Subscriptions" element={<ProtectedRoute><FozzilAccount /></ProtectedRoute>} />
        <Route path="/Devices" element={<ProtectedRoute><FozzilAccount /></ProtectedRoute>} />
        <Route path="/Appearance" element={<ProtectedRoute><FozzilAccount /></ProtectedRoute>} />
        <Route path="/Privacy" element={<ProtectedRoute><FozzilAccount /></ProtectedRoute>} />
        <Route path="/payment-required" element={<PaymentRequired />} />
        <Route path="/shop" element={<StoreLanding />} />
        <Route path="/shop/buy-ark" element={<ShopArk />} />
        <Route path="/shop/buy-ark/ark" element={<BuyArk />} />
        <Route path="/shop/buy-ark/ark-pro" element={<BuyArkPro />} />
        <Route path="/shop/medos" element={<ShopMedos />} />
        <Route path="/shop/intelligence" element={<ShopIntelligence />} />
        <Route path="/shop/bag" element={<BagPage />} />
        <Route path="/shop/checkout" element={<ProtectedRoute><CheckoutPage /></ProtectedRoute>} />
        <Route path="/shop/orders" element={<ProtectedRoute><OrdersPage /></ProtectedRoute>} />
        <Route path="/shop/saves" element={<ProtectedRoute><SavesPage /></ProtectedRoute>} />
        <Route path="*" element={null} />
      </Routes>
      {!hideFooter && <Footer minimal={location.pathname === "/account"} footerRef={footerRef} />}
      <CookieConsent />
    </div>
  );
}

function App() {
  return (
    <>
    <RegionProvider>
      <BagProvider>
        <AuthProvider>
          <SavesProvider>
            <Router>
              <AppContent />
            </Router>
          </SavesProvider>
        </AuthProvider>
      </BagProvider>
    </RegionProvider>
      <style>{`
/* Font styles moved to index.css for global consistency */

/* Reset and Global Base Styles handled in index.css */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body,
html {
  min-height: 100vh;
}

.App {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

a {
  text-decoration: none;
  color: inherit;
}

ul {
  list-style: none;
}

img {
  max-width: 100%;
  height: auto;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.App-logo {
  height: 40vmin;
  pointer-events: none;
}

@media (prefers-reduced-motion: no-preference) {
  .App-logo {
    animation: App-logo-spin infinite 20s linear;
  }
}

.App-header {
  background-color: #282c34;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: calc(10px + 2vmin);
  color: black;
}

.App-link {
  color: #61dafb;
}

@keyframes App-logo-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
      `}</style>
    </>
  );
}

export default App;
